import Vue from 'vue'
import api, { unsubscribe } from '@/api'
import user from '@/api/modules/user'
import Cookie from '@/utils/cookie'
import router from '@/router'
import { checkAdvisoryRole, checkPresenterRole, checkStatisticsOnlyRole } from '@/utils/roles'

const getDefaultState = () => {
  return {
    accessToken: Cookie.get('accessToken') || null,
    accessTokenExpirationTime: null,
    refreshToken: Cookie.get('refreshToken') || null,
    hasRole: Cookie.get('hasRole') || null,
    expiresIn: null,
    authExpire: Cookie.get('authExpire') || null,
    user: null,
    logoutUrl: { name: 'login' },
    redirectRoute: null,
    hasSeenWelcomePage: false,
    timestampLastCallApi: null,
  }
}

const state = getDefaultState()

function setTokens(state, data) {
  Vue.set(state, 'accessToken', data.accessToken)
  Vue.set(state, 'refreshToken', data.refreshToken)
  // Expiration time must be in seconds
  const expiration = data.expiresIn || 7200
  Vue.set(state, 'expiresIn', expiration)

  let d = new Date()
  d.setSeconds(d.getSeconds() + expiration)

  Vue.set(state, 'authExpire', d)
  Vue.set(state, 'accessTokenExpirationTime', data.expiresIn)

  Cookie.set('accessToken', data.accessToken, d)
  Cookie.set('refreshToken', data.refreshToken, d)
  Cookie.set('authExpire', d, d)
}

const actions = {
  setTimestampLastCallApi (context, timestamp) {
    context.commit('setTimestampLastCallApi', timestamp)
  },

  fetchUser (context, cached = true) {
    if (cached && state.user) {
      return Promise.resolve()
    } else {
      return user.fetch()
        .then((data) => {
          context.commit('saveUserData', data)
          context.commit('user/fetch', data, {root: true})
          context.commit('setRoleCookie')
          return Promise.resolve()
        })
        .catch(({response}) => {
          if (response.status === 401) {
            context.commit('resetState')

            router.push({ name: 'login' })
            return Promise.reject(response.status)
          }
        })
    }
  },

  setShownTutorials (context, shownTutorials) {
    return user.updateShownTutorials(shownTutorials)
    .then(() => {
      context.commit('saveShownTutorials', shownTutorials)
      return Promise.resolve()
    })
    .catch(response => {
      return Promise.reject(response)
    })
  },

  signInKeycloakToken (context, { token }) {
    return user.signInKeycloakToken(token).then((data) => {
      context.commit('saveTokensWithSetHeaderApi', data)
      return Promise.resolve()
    })
  },

  logIn (context, { email, password, disableSaveTokens = false, frcCaptchaResponse = '' }) {
    return user.signIn({ email, password, frcCaptchaResponse })
      .then(data => {
        if (!disableSaveTokens) {
          context.commit('saveTokensWithSetHeaderApi', data)
        }
        return Promise.resolve()
      })
      .catch(response => {
        return Promise.reject(response)
      })
  },

  logInToken (context, credentials) {
    return user.signInToken(credentials)
      .then(data => {
        context.commit('saveTokensWithSetHeaderApi', data)
        return Promise.resolve()
      })
      .catch(response => {
        return Promise.reject(response)
      })
  },

  logInSsoToken (context, credentials) {
    return user.retrieveTokensCollaborator(credentials)
      .then(data => {
        context.commit('saveTokensWithSetHeaderApi', data)
        return Promise.resolve()
      })
      .catch(response => {
        return Promise.reject(response)
      })
  },

  confirmIdentity (context, id) {
    return user.confirmIdentity(id)
      .then(data => {
        return Promise.resolve(data.nextStep)
      })
      .catch(response => {
        return Promise.reject(response)
      })
  },

  smsVerification (context, params) {
    return user.smsVerification(params)
      .then(data => {
        context.commit('saveTokensWithSetHeaderApi', data)
        return Promise.resolve()
      })
      .catch(response => {
        return Promise.reject(response)
      })
  },


  logOut (context) {
    api.defaults.headers.common['Authorization'] = null

    return user.signOut(state.accessToken)
      .then(() => {
        context.dispatch('reset', null, {root: true})
        return Promise.resolve()
      })
      .catch(({response}) => {
        context.dispatch('reset', null, {root: true})
        return Promise.reject(response)
      })
      .finally(() => {
        unsubscribe()
        const { logoutUrl } = context.state

        if (logoutUrl) {
          if (typeof logoutUrl === 'object') {
            router.push(logoutUrl)
          } else {
            window.location.href = logoutUrl
          }
        }
      })
  },

  refreshToken (context) {
    return user.refreshToken(context.state.refreshToken)
      .then(data => {
        context.commit('saveTokensWithSetHeaderApi', data)
        context.commit('setRoleCookie')
      })
  },

  confirmUser (context, code) {
    return user.confirm(code)
      .then((response) => {
        if (response.message === 'user.validated') {
          context.commit('confirmUser')
          context.commit('setRoleCookie')
        }
        return Promise.resolve()
      })
      .catch((response) => {
        return Promise.reject(response)
      })
  },

  setLogoutUrl (context) {
    return user.getLogoutUrl()
      .then(data => {
        context.commit('setLogoutUrl', data)
        return Promise.resolve()
      })
      .catch(response => {
        return Promise.reject(response)
      })
  },

  setRedirectRoute (context, route) {
    context.commit('setRedirectRoute', route)
  },

  resetState (context) {
    context.commit('resetState')
  }
}

const mutations = {
  setTimestampLastCallApi (state, timestamp) {
    state.timestampLastCallApi = timestamp
  },
  
  saveTokens (state, data) {
    setTokens(state, data)
  },

  saveTokensWithSetHeaderApi(state, data) {
    setTokens(state, data)
    api.defaults.headers.common['Authorization'] = `Bearer ${data.accessToken}`
  },

  saveUserData (state, data) {
    Vue.set(state, 'user', data)
  },

  saveShownTutorials (state, data) {
    Vue.set(state.user, 'shownTutorials', data)
  },

  setLogoutUrl (state, result) {
    Vue.set(state, 'logoutUrl', result.url)
  },

  setRedirectRoute (state, route) {
    Vue.set(state, 'redirectRoute', route)
  },

  setRoleCookie (state) {
    let d = new Date()
    d.setSeconds(d.getSeconds() + state.expiresIn)

    if (state.user.state === 'unconfirmed') {
      // This is to make sure we confirm users first!
      Vue.set(state, 'hasRole', 'unconfirmed')
      Cookie.set('hasRole', 'unconfirmed', d)
    } else {
      if (checkAdvisoryRole(state.user.roles) || checkStatisticsOnlyRole(state.user.roles)) {
        Vue.set(state, 'hasRole', 'advisor')
        Cookie.set('hasRole', 'advisor', d)
      } else {
        Vue.set(state, 'hasRole', 'customer')
        Cookie.set('hasRole', 'customer', d)
      }
    }
  },

  confirmUser (state) {
    Vue.set(state.user, 'state', 'confirmed')
  },

  resetState (state) {
    Cookie.remove('accessToken')
    Cookie.remove('refreshToken')
    Cookie.remove('hasRole')
    Cookie.remove('authExpire')

    // We delete the token to prevent the back from replying with a 401
    delete api.defaults.headers.common['Authorization']

    const newState = getDefaultState()
    newState.logoutUrl = state.logoutUrl
    newState.redirectRoute = state.redirectRoute

    Object.assign(state, newState)
  }
}

const getters = {
  authenticated (state) {
    return Boolean(state.accessToken)
  },

  authExpire (state) {
    return state.authExpire
  },

  accessToken (state) {
    return state.accessToken
  },

  hasRole (state) {
    return state.hasRole
  },

  hasSeenWelcomePage(state) {
    return state?.user?.shownTutorials?.hasSeenWelcomePage
  },

  refreshToken (state) {
    return state.refreshToken
  },

  isAdvisor (state) {
    return state.hasRole === 'advisor'
  },

  isCustomer (state) {
    if (state.user) {
      return Boolean(state.user.roles.filter(role => role === 'customer').length)
    } else {
      return false
    }
  },

  isAdmin (state) {
    if (state.user) {
      return Boolean(state.user.roles.filter(role => role === 'admin').length)
    } else {
      return false
    }
  },

  isPresenter (state) {
    if (state.user) {
      return checkPresenterRole(state.user.roles)
    } else {
      return false
    }
  }
}

export default () => ({
  namespaced: true,
  state,
  actions,
  mutations,
  getters
})
